import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Brain, Clock, CheckCircle2, AlertCircle, 
  ExternalLink, FileText, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MinhasAvaliacoes = () => {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${API_URL}/employee-portal/my-assessments`);
      setAssessments(response.data.assessments || []);
      setStats(response.data.stats || { total: 0, completed: 0, pending: 0 });
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast.error('Erro ao carregar suas avaliações');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'respondida':
      case 'concluida':
        return <Badge className="bg-green-500"><CheckCircle2 size={12} className="mr-1" /> Respondida</Badge>;
      case 'pendente':
        return <Badge className="bg-amber-500"><Clock size={12} className="mr-1" /> Pendente</Badge>;
      case 'expirada':
        return <Badge className="bg-red-500"><AlertCircle size={12} className="mr-1" /> Expirada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeLabel = (tipo) => {
    const types = {
      'psicossocial': 'Psicossocial',
      'ergonomica': 'Ergonômica',
      'riscos': 'Riscos',
      'clima': 'Clima Organizacional',
      'satisfacao': 'Satisfação',
      'onboarding': 'Onboarding',
      'offboarding': 'Offboarding'
    };
    return types[tipo] || tipo;
  };

  const handleResponder = (assessment) => {
    if (assessment.link || assessment.token) {
      const url = assessment.link || `${window.location.origin}/avaliar/${assessment.token}`;
      window.open(url, '_blank');
    } else {
      toast.info('Link da avaliação não disponível. Entre em contato com o RH.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="minhas-avaliacoes-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Minhas Avaliações
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Olá, {user?.name || 'Funcionário'}! Confira suas avaliações pendentes.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText size={24} />
              <div>
                <p className="text-sm opacity-80">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={24} />
              <div>
                <p className="text-sm opacity-80">Respondidas</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={24} />
              <div>
                <p className="text-sm opacity-80">Pendentes</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Assessments Alert */}
      {stats.pending > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-amber-600" size={24} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Você tem {stats.pending} avaliação(ões) pendente(s)
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Por favor, responda as avaliações pendentes para manter sua conformidade.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assessment List */}
      {assessments.length > 0 ? (
        <div className="grid gap-4">
          {assessments.map((assessment) => (
            <Card 
              key={assessment.id} 
              className={`hover:shadow-md transition-shadow ${
                assessment.status === 'pendente' ? 'border-l-4 border-l-amber-500' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Brain size={20} className="text-purple-600" />
                      <h3 className="font-semibold text-lg">{assessment.titulo || assessment.nome}</h3>
                      {getStatusBadge(assessment.status)}
                      <Badge variant="outline">{getTypeLabel(assessment.tipo)}</Badge>
                    </div>
                    
                    {assessment.descricao && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        {assessment.descricao}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      {assessment.enviado_em && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Enviada em: {new Date(assessment.enviado_em).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                      {assessment.respondido_em && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 size={14} />
                          Respondida em: {new Date(assessment.respondido_em).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {assessment.status === 'pendente' && (
                    <Button 
                      onClick={() => handleResponder(assessment)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Responder
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Brain size={48} className="mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">
              Nenhuma avaliação encontrada
            </h3>
            <p className="text-slate-500">
              Você não tem avaliações atribuídas no momento.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MinhasAvaliacoes;
